import { z } from "zod";

const colorRowSchema = z.object({
  id: z.any().optional().nullable(),
  name: z.string().optional().nullable(),
  name_ur: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  color_ur: z.string().optional().nullable(),
  colorCode: z.string().optional().nullable(),
  price: z.any().optional().nullable(),
  weight: z.any().optional().nullable(),
  dimension: z.any().optional().nullable(),
  unit: z.string().optional().nullable(),
  unit_ur: z.string().optional().nullable(),
  moq: z.any().optional().nullable(),
  position: z.any().optional().nullable(),
  existingImages: z.array(z.any()).optional().default([]),
  newImageFiles: z.array(z.any()).optional().default([]),
  newImagePositions: z.array(z.any()).optional().default([]),
  existingVariantImage: z.any().optional().nullable(),
  variantImageFile: z.any().optional().nullable(),
  texture: z.boolean().optional().default(false),
  existingTextureImage: z.any().optional().nullable(),
  textureImageFile: z.any().optional().nullable(),
  textureCode: z.string().optional().nullable(),
});

export const productFormSchema = z
  .object({
    categoryIds: z.array(z.union([z.number(), z.string()])).default([]),
    name: z.string().trim().min(1, "Product name is required"),
    productName_ur: z.string().optional().nullable(),
    customUrl: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    description_ur: z.string().optional().nullable(),
    shortDescription: z.string().optional().nullable(),
    shortDescription_ur: z.string().optional().nullable(),
    status: z.string().optional().nullable(),
    colors: z.array(colorRowSchema).default([]),
    currency: z.string().optional().nullable(),
    brandId: z.union([z.string(), z.number()]).optional().nullable(),
    dealership: z.string().optional().nullable(),
    dealership_ur: z.string().optional().nullable(),
    singleVarinet: z.boolean().default(false),
    weight: z.any().optional().nullable(),
    unit: z.string().optional().nullable(),
    unit_ur: z.string().optional().nullable(),
    price: z.any().optional().nullable(),
    dimensions: z.any().optional().nullable(),
    moq: z.any().optional().nullable(),
    technicalDataSheet: z.any().optional().nullable(),
    existingTechnicalDataSheet: z.any().optional().nullable(),
    video: z.any().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    const plainDescription = String(data.description || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!plainDescription) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Description is required",
        path: ["description"],
      });
    }

    if (!data.categoryIds || data.categoryIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one category is required",
        path: ["categoryIds"],
      });
    }

    if (!data.colors || data.colors.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one product variant is required",
        path: ["colors"],
      });
      return;
    }

    data.colors.forEach((variant, index) => {
      const isTempName =
        typeof variant.name === "string" &&
        variant.name.startsWith("__NEW_VARIANT_");
      const hasName =
        variant.name &&
        String(variant.name).trim() !== "" &&
        !isTempName;

      if (!hasName) {
        // Single-variant: only flag the first row (shared name across colors)
        if (!(data.singleVarinet && index !== 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Variant name is required",
            path: ["colors", index, "name"],
          });
        }
      }

      const hasColor =
        variant.color && String(variant.color).trim() !== "";
      const hasVariantImage = !!(
        variant.variantImageFile || variant.existingVariantImage
      );
      const totalImages =
        (variant.existingImages?.length || 0) +
        (variant.newImageFiles?.length || 0);

      if (data.singleVarinet) {
        if (!hasColor && totalImages === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Either color or at least one product image is required",
            path: ["colors", index, "color_or_image"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Color is required when no product image is provided",
            path: ["colors", index, "color"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one image is required when color is not provided",
            path: ["colors", index, "images"],
          });
        }
      } else {
        if (!hasColor && totalImages === 0 && !hasVariantImage) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Either color, product images, or variant image is required",
            path: ["colors", index, "color_or_image"],
          });
          if (!hasColor) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Color is required when images and variant image are not provided",
              path: ["colors", index, "color"],
            });
          }
          if (totalImages === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Product images are required when color and variant image are not provided",
              path: ["colors", index, "images"],
            });
          }
          if (!hasVariantImage) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Variant image is required when color and product images are not provided",
              path: ["colors", index, "variant_image"],
            });
          }
        }
      }
    });
  });

/**
 * Flat error map matching the previous validationErrors keys for UI reuse.
 */
export function flattenProductFormErrors(errors) {
  const flat = {};
  if (!errors) return flat;

  if (errors.categoryIds?.message) {
    flat.categories = errors.categoryIds.message;
  }
  if (errors.description?.message) {
    flat.description = errors.description.message;
  }
  if (errors.colors?.message) {
    flat.variants = errors.colors.message;
  }
  if (errors.colors?.root?.message) {
    flat.variants = errors.colors.root.message;
  }
  if (errors.name?.message) {
    flat.name = errors.name.message;
  }
  if (errors.currency?.message) {
    flat.currency = errors.currency.message;
  }

  if (Array.isArray(errors.colors)) {
    errors.colors.forEach((colorErr, index) => {
      if (!colorErr || typeof colorErr !== "object") return;
      if (colorErr.name?.message) {
        flat[`variant_${index}_name`] = colorErr.name.message;
      }
      if (colorErr.color?.message) {
        flat[`variant_${index}_color`] = colorErr.color.message;
      }
      if (colorErr.color_or_image?.message) {
        flat[`variant_${index}_color_or_image`] =
          colorErr.color_or_image.message;
      }
      if (colorErr.variant_image?.message) {
        flat[`variant_${index}_variant_image`] =
          colorErr.variant_image.message;
      }
      if (colorErr.images?.message) {
        flat[`variant_${index}_images`] = colorErr.images.message;
      }
    });
    return flat;
  }

  if (errors.colors && typeof errors.colors === "object") {
    Object.keys(errors.colors).forEach((key) => {
      if (key === "message" || key === "root" || key === "type" || key === "ref") {
        return;
      }
      const index = Number(key);
      if (Number.isNaN(index)) return;
      const colorErr = errors.colors[key];
      if (!colorErr) return;
      if (colorErr.name?.message) {
        flat[`variant_${index}_name`] = colorErr.name.message;
      }
      if (colorErr.color?.message) {
        flat[`variant_${index}_color`] = colorErr.color.message;
      }
      if (colorErr.color_or_image?.message) {
        flat[`variant_${index}_color_or_image`] =
          colorErr.color_or_image.message;
      }
      if (colorErr.variant_image?.message) {
        flat[`variant_${index}_variant_image`] =
          colorErr.variant_image.message;
      }
      if (colorErr.images?.message) {
        flat[`variant_${index}_images`] = colorErr.images.message;
      }
    });
  }

  return flat;
}

export default productFormSchema;
